import { useEffect, useState } from 'react';
import isImageAttachment from '../../../helpers/isImageAttachment';
import isVideoAttachment from '../../../helpers/isVideoAttachment';
import { LIGHTBOX_BODY_PADDING, LIGHTBOX_HEADER_PADDING } from './lightboxSafeArea';

export interface AttachmentLike {
  name?: string;
  mimetype?: string;
  url?: string;
}

interface SupportAttachmentsProps {
  attachments: AttachmentLike[];
  /** Resolves the display name for an attachment (callers may have richer logic). */
  resolveName: (attachment: AttachmentLike, index: number) => string;
}

const CARD_WIDTH = 200;
const GRID_GAP = 14;
const LINE = '#e9ecef';
const LINE_HOVER = '#bfc8d4';
const INK = '#2b3142';
const INK_MUTE = '#8b94a3';

type FileKind = { ext: string; color: string; icon: string };

const FILE_TYPES: Record<string, { color: string; icon: string }> = {
  pdf: { color: '#e85d75', icon: 'fa-file-pdf' },
  doc: { color: '#3b7ddd', icon: 'fa-file-word' },
  docx: { color: '#3b7ddd', icon: 'fa-file-word' },
  xls: { color: '#22a06b', icon: 'fa-file-excel' },
  xlsx: { color: '#22a06b', icon: 'fa-file-excel' },
  csv: { color: '#22a06b', icon: 'fa-file-csv' },
  zip: { color: '#f0a02a', icon: 'fa-file-archive' },
  rar: { color: '#f0a02a', icon: 'fa-file-archive' },
  txt: { color: '#6c757d', icon: 'fa-file-lines' },
  mp4: { color: '#7b5cd6', icon: 'fa-file-video' },
  mov: { color: '#7b5cd6', icon: 'fa-file-video' },
  webm: { color: '#7b5cd6', icon: 'fa-file-video' },
  m4v: { color: '#7b5cd6', icon: 'fa-file-video' },
};

const playBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(15,20,32,.28)',
  color: '#fff',
  fontSize: 28,
  pointerEvents: 'none',
};

const extensionOf = (value?: string): string => {
  if (!value) return '';
  const base = value.split('#')[0].split('?')[0].split('/').filter(Boolean).pop() || '';
  const dot = base.lastIndexOf('.');
  return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
};

const fileKindOf = (attachment: AttachmentLike, displayName: string): FileKind => {
  const ext = extensionOf(displayName) || extensionOf(attachment.name) || extensionOf(attachment.url);
  const type = FILE_TYPES[ext];
  if (type) return { ext, ...type };
  return { ext, color: '#6c757d', icon: 'fa-file' };
};

/**
 * Truncates the middle of a filename, always preserving the extension.
 * e.g. "1a02fcc8-54d5-413a-bcc0-b451b05f0dc8.pdf" -> "1a02fcc8-54d5-…0dc8.pdf"
 */
const middleTruncate = (name: string, max = 26): string => {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot) : '';
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const room = max - ext.length - 1; // 1 for the ellipsis
  if (room < 4) return `…${name.slice(-(max - 1))}`;
  const head = Math.ceil(room * 0.6);
  const tail = room - head;
  return `${stem.slice(0, head)}…${stem.slice(stem.length - tail)}${ext}`;
};

const cardBaseStyle = (hovered: boolean): React.CSSProperties => ({
  width: CARD_WIDTH,
  border: `1px solid ${hovered ? LINE_HOVER : LINE}`,
  borderRadius: 8,
  overflow: 'hidden',
  background: '#fff',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'border-color .15s, box-shadow .15s, transform .15s',
  transform: hovered ? 'translateY(-1px)' : 'none',
  boxShadow: hovered ? '0 6px 14px rgba(20,30,50,.08)' : 'none',
  display: 'block',
});

const previewAreaStyle: React.CSSProperties = {
  aspectRatio: '16 / 11',
  width: '100%',
  background: '#f4f6f9',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const footerStyle: React.CSSProperties = {
  padding: '10px 12px 11px',
  borderTop: `1px solid ${LINE}`,
};

const SupportAttachments = ({ attachments, resolveName }: SupportAttachmentsProps): JSX.Element | null => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; name: string; isVideo: boolean } | null>(null);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: GRID_GAP }}>
        {attachments.map((attachment, index) => {
          const name = resolveName(attachment, index);
          const key = `${attachment.url || attachment.name || 'att'}-${index}`;
          const hovered = hoveredKey === key;
          const truncated = middleTruncate(name);
          const isImage = isImageAttachment(attachment);
          const isVideo = !isImage && isVideoAttachment(attachment);

          const footer = (
            <div style={footerStyle}>
              <div
                title={name}
                style={{ color: INK, fontWeight: 700, fontSize: 12.5, lineHeight: 1.3, wordBreak: 'break-word' }}
              >
                {truncated}
              </div>
              {!isImage && (
                <div
                  style={{ color: INK_MUTE, fontWeight: 600, fontSize: 10.5, marginTop: 3, textTransform: 'uppercase' }}
                >
                  {fileKindOf(attachment, name).ext || 'file'}
                </div>
              )}
            </div>
          );

          if (isImage || isVideo) {
            return (
              <button
                key={key}
                type="button"
                aria-label={`Open attachment ${name}`}
                onClick={() => setLightbox({ url: attachment.url || '', name, isVideo })}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey((prev) => (prev === key ? null : prev))}
                style={{ ...cardBaseStyle(hovered), padding: 0, textAlign: 'left' }}
              >
                <div style={{ ...previewAreaStyle, position: 'relative' }}>
                  {isVideo ? (
                    <>
                      <video
                        src={attachment.url}
                        preload="metadata"
                        muted
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <span style={playBadgeStyle}>
                        <i className="fas fa-play-circle" aria-hidden="true" />
                      </span>
                    </>
                  ) : (
                    <img
                      src={attachment.url}
                      alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  )}
                </div>
                {footer}
              </button>
            );
          }

          const kind = fileKindOf(attachment, name);
          return (
            <a
              key={key}
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open attachment ${name}`}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey((prev) => (prev === key ? null : prev))}
              style={cardBaseStyle(hovered)}
            >
              <div style={previewAreaStyle}>
                <i className={`fas ${kind.icon}`} style={{ fontSize: 40, color: kind.color }} aria-hidden="true" />
              </div>
              {footer}
            </a>
          );
        })}
      </div>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.name}
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            background: 'rgba(15,20,32,.78)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: LIGHTBOX_HEADER_PADDING,
              color: '#fff',
            }}
          >
            <span
              title={lightbox.name}
              style={{
                fontWeight: 700,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {lightbox.name}
            </span>
            <span style={{ display: 'inline-flex', gap: 8, flexShrink: 0 }}>
              <a
                href={lightbox.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="btn btn-sm btn-light"
                title="Open in a new tab"
              >
                <i className="fas fa-external-link-alt" />
              </a>
              <a
                href={lightbox.url}
                download={lightbox.name}
                onClick={(e) => e.stopPropagation()}
                className="btn btn-sm btn-light"
                title="Download"
              >
                <i className="fas fa-download" />
              </a>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="btn btn-sm btn-light"
                title="Close (Esc)"
              >
                <i className="fas fa-times" />
              </button>
            </span>
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: LIGHTBOX_BODY_PADDING,
              minHeight: 0,
            }}
          >
            {lightbox.isVideo ? (
              <video
                src={lightbox.url}
                controls
                autoPlay
                playsInline
                style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6 }}
              />
            ) : (
              <img
                src={lightbox.url}
                alt={lightbox.name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportAttachments;
